import { useEffect, useRef, useState } from 'react';
import type { CSSProperties, KeyboardEvent } from 'react';
import type { Slot } from '../../../engine';
import type { Chip } from '../../game/viewModel';
import { cardAria } from '../../game/viewModel';
import { Card3D, FlatCard } from '../Card';
import { HintSeal } from './Piles';

export function MyGrid({
  grid,
  seat,
  cw,
  targetSlots,
  slotAction,
  aimed,
  ghostValue,
  chips,
  pairs,
  flipDelays,
  dealing,
  forming,
  flash,
  shakeSlot,
  focusKey,
  onSlot,
  onHover,
  hintSlot = null,
}: {
  grid: Slot[];
  seat: number;
  cw: number;
  targetSlots: Set<number>;
  slotAction: 'flipInitial' | 'swap' | 'flip' | null;
  aimed: number | null;
  ghostValue: number | null;
  chips: Chip[] | null;
  pairs: Set<number>;
  flipDelays: Map<number, number> | null;
  dealing: boolean;
  /** Columns whose pair just formed → ms to wait before the one-time shimmer (hole-end flips land later). */
  forming: Map<number, number>;
  flash: Set<number>;
  shakeSlot: number | null;
  /** Changes when a new step starts; keyboard users get focus on the first legal slot. */
  focusKey: string | null;
  onSlot: (slot: number) => void;
  onHover: (slot: number | null) => void;
  /** The slot the hint marks (game/hints.ts). */
  hintSlot?: number | null;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const [focusIdx, setFocusIdx] = useState(0);

  // Focus follows the step (keyboard users only; see TableScreen).
  useEffect(() => {
    if (!focusKey) return;
    const first = [0, 1, 2, 3, 4, 5, 6, 7].find((s) => targetSlots.has(s));
    if (first === undefined) return;
    setFocusIdx(first);
    refs.current[first]?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusKey]);

  const move = (e: KeyboardEvent, slot: number) => {
    let next = slot;
    if (e.key === 'ArrowRight') next = slot % 4 === 3 ? slot : slot + 1;
    else if (e.key === 'ArrowLeft') next = slot % 4 === 0 ? slot : slot - 1;
    else if (e.key === 'ArrowDown') next = slot < 4 ? slot + 4 : slot;
    else if (e.key === 'ArrowUp') next = slot >= 4 ? slot - 4 : slot;
    else return;
    e.preventDefault();
    setFocusIdx(next);
    refs.current[next]?.focus();
  };

  const anyTarget = targetSlots.size > 0;

  const slotButton = (slot: number) => {
    const s = grid[slot];
    const target = targetSlots.has(slot);
    const isAimed = aimed === slot;
    const card = s?.card ?? null;
    const delay = flipDelays?.get(slot) ?? 0;
    const rock = target && slotAction === 'flip';
    return (
      <button
        key={slot}
        ref={(el) => {
          refs.current[slot] = el;
        }}
        type="button"
        className={`slot${target ? ' target' : ''}${anyTarget && !target ? ' dim' : ''}${isAimed ? ' aimed' : ''}${rock ? ' rock' : ''}${
          shakeSlot === slot ? ' shake' : ''
        }${flash.has(slot) ? ' flash' : ''}${dealing ? ' dealing' : ''}${hintSlot === slot ? ' hinted' : ''}`}
        style={{ '--i': slot } as CSSProperties}
        tabIndex={slot === focusIdx ? 0 : -1}
        data-slot-key={`${seat}:${slot}`}
        aria-label={`${cardAria('Your', slot, s)}${pairs.has(slot % 4) ? ', matched column' : ''}${target ? (slotAction === 'swap' ? '. Swap here.' : '. Flip it.') : ''}`}
        aria-disabled={!target}
        aria-keyshortcuts={String(slot + 1)}
        onClick={() => onSlot(slot)}
        onFocus={(e) => {
          setFocusIdx(slot);
          if (e.target.matches(':focus-visible')) onHover(slot);
        }}
        onBlur={() => onHover(null)}
        onKeyDown={(e) => move(e, slot)}
        onPointerEnter={(e) => e.pointerType === 'mouse' && onHover(slot)}
        onPointerLeave={(e) => e.pointerType === 'mouse' && onHover(null)}
      >
        <Card3D value={card?.value ?? null} faceUp={!!s?.faceUp} width={cw} flipDelay={delay} {...(card && s?.faceUp ? { flightId: card.id } : {})} />
        {isAimed && ghostValue !== null && (
          <span className="ghost" aria-hidden="true">
            <FlatCard value={ghostValue} width={cw} peek={false} />
          </span>
        )}
        {hintSlot === slot && <HintSeal />}
      </button>
    );
  };

  return (
    <div className="mygrid" style={{ '--cw': `${cw}px` } as CSSProperties} role="group" aria-label="Your cards" data-coach="grid">
      {[0, 1, 2, 3].map((c) => {
        const chip = chips?.[c];
        const paired = pairs.has(c);
        const m5 = paired && grid[c]?.card?.value === -5;
        const formDelay = forming.get(c);
        return (
          <div
            key={c}
            className={`col${paired ? ' paired' : ''}${m5 ? ' m5' : ''}${paired && chip?.bonus ? ' bonus' : ''}${
              paired && formDelay !== undefined ? ' forming' : ''
            }`}
            style={formDelay !== undefined ? ({ '--form-delay': `${formDelay}ms` } as CSSProperties) : undefined}
            data-coach={`col${c}`}
          >
            <div className="col-cards">
              {slotButton(c)}
              {slotButton(c + 4)}
              {paired && <span className="pair-frame" aria-hidden="true" />}
            </div>
            <span className={`chip ${chip?.kind ?? 'none'}${chip?.bonus ? ' bonus' : ''}`} aria-hidden="true">
              {chip?.label}
            </span>
          </div>
        );
      })}
    </div>
  );
}
